import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Search, Globe, Package, Layers, Link2,
  Radio, GitCompare, Tag, CheckCircle, XCircle, AlertTriangle,
  Activity, Lock, RefreshCw, Bell, Database, BrainCircuit, Newspaper
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';

// ─── Tool Usage card config — labels/units shown to users; internal tool keys never surface ──
const USAGE_TOOLS = {
  crawler:       { label: 'Crawler',              unit: 'bundles crawled',   Icon: Globe },
  bundle_lookup: { label: 'Bundle Lookup',        unit: 'bundles looked up', Icon: Search },
  schain:        { label: 'Schain Validator',     unit: 'schains validated', Icon: Link2 },
  sellers_json:  { label: 'Sellers.json Checker', unit: 'files checked',     Icon: Database },
  ads_txt:       { label: 'Ads.txt Checker',      unit: 'files checked',     Icon: FileText },
  app_category:  { label: 'App Category',         unit: 'bundles segmented', Icon: Tag },
  bid_request:   { label: 'Bid Request Analyzer', unit: 'samples analyzed',  Icon: GitCompare },
};
const USAGE_RANGES = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['last_7_days', 'Last 7 days'],
  ['this_month', 'This month'], ['last_month', 'Last month'],
  ['this_quarter', 'This quarter'], ['prev_quarter', 'Prev quarter'],
];
function fmtDuration(ms) {
  ms = Number(ms) || 0;
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
  if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  return `${Math.floor(ms / 3600000)}h ${Math.round((ms % 3600000) / 60000)}m`;
}

// ─── Tool health check config ─────────────────────────────────────────────────

const TOOLS = [
  {
    path: '/ads-txt',
    label: 'Ads.txt Checker',
    icon: FileText,
    tier: 'free',
    color: 'cyan',
    description: 'Validate ads.txt and app-ads.txt files for IAB compliance, duplicates, and integrity',
    healthCheck: async () => {
      const { data } = await axios.post('/api/ads-txt/check', { domain: 'google.com' }, { headers: { 'X-Health-Check': '1' } });
      if (!data || typeof data.success === 'undefined') throw new Error('Bad response');
    }
  },
  {
    path: '/sellers-json',
    label: 'Sellers.json',
    icon: Search,
    tier: 'pro',
    color: 'green',
    description: 'Validate sellers.json files, look up seller IDs and cross-check supply chain integrity',
    healthCheck: async () => {
      const { data } = await axios.post('/api/sellers-json/check', { domain: 'google.com' }, { headers: { 'X-Health-Check': '1' } });
      if (!data) throw new Error('Bad response');
    }
  },
  {
    path: '/crawler',
    label: 'Ads.txt Crawler',
    icon: Globe,
    tier: 'pro',
    color: 'amber',
    description: 'Bulk crawl domains or bundle IDs and search for specific ads.txt lines at scale',
    healthCheck: async () => {
      const { data } = await axios.get('/api/crawler/jobs');
      if (!Array.isArray(data)) throw new Error('Bad response');
    }
  },
  {
    path: '/bundles',
    label: 'Bundle Tracker',
    icon: Package,
    tier: 'pro',
    color: 'purple',
    description: 'Manage approved and rejected app bundle lists across DSPs with change history',
    healthCheck: async () => {
      const { data } = await axios.get('/api/bundles/lists');
      if (!Array.isArray(data)) throw new Error('Bad response');
    }
  },
  {
    path: '/bundle-lookup',
    label: 'Bundle Lookup',
    icon: Layers,
    tier: 'pro',
    color: 'cyan',
    description: 'Find app names, store URLs and developer domains from iOS and Android bundle IDs',
    healthCheck: async () => {
      const { data } = await axios.post('/api/bundle-lookup/lookup', { bundleIds: ['com.test'] }, { headers: { 'X-Health-Check': '1' } });
      if (!data || !data.results) throw new Error('Bad response');
    }
  },
  {
    path: '/schain',
    label: 'Schain Validator',
    icon: Link2,
    tier: 'pro',
    color: 'red',
    description: 'Validate supply chain (schain) objects, verify node authorization and sellers.json entries',
    healthCheck: async () => {
      const { data } = await axios.post('/api/schain/validate', { schain: { complete: 1, ver: '1.0', nodes: [] } }, { headers: { 'X-Health-Check': '1' } });
      if (!data) throw new Error('Bad response');
    }
  },
  {
    path: '/bid-request',
    label: 'Bid req Analyzer',
    icon: Radio,
    tier: 'pro',
    color: 'cyan',
    description: 'Analyze OpenRTB 2.5/2.6 bid requests for compliance, privacy signals and schain integrity',
    healthCheck: async () => {
      const { data } = await axios.post('/api/bid-request/analyze', { bidRequest: {} }, { headers: { 'X-Health-Check': '1' } });
      if (!data) throw new Error('Bad response');
    }
  },
  {
    path: '/disc-monitor',
    label: 'Discrepancy Monitor',
    icon: Activity,
    tier: 'pro',
    color: 'cyan',
    description: 'Monitor Impressions and Revenue discrepancies between your Ad Server and partners — with file import or automatic twice-daily API scanning',
    healthCheck: async () => {
      const { data } = await axios.get('/api/discrepancy-monitor/list');
      if (!Array.isArray(data)) throw new Error('Bad response');
    }
  },
  {
    path: '/app-category',
    label: 'App Category Segmenter',
    icon: Tag,
    tier: 'pro',
    color: 'purple',
    description: 'Paste bundle IDs and instantly segment them by app category — Games, Finance, Health and more — with a visual distribution chart',
    healthCheck: async () => {
      const { data } = await axios.post('/api/app-category/segment', { bundleIds: ['com.test.app'] }, { headers: { 'X-Health-Check': '1' } });
      if (!data || !data.segments) throw new Error('Bad response');
    }
  },
  {
    path: '/discrepancy',
    label: 'Discrepancy Checker',
    icon: GitCompare,
    tier: 'pro',
    color: 'amber',
    description: 'Compare reporting numbers between two parties and identify billing discrepancies by date or bundle',
    healthCheck: async () => {
      await axios.post('/api/discrepancy/preview', { input: { type: 'csv', data: 'date,wins\n2024-01-01,100', name: 'test' } });
    }
  },
  {
    path: '/fetch-engine',
    label: 'Fetch Engine',
    icon: Database,
    tier: 'pro',
    color: 'purple',
    beta: true,
    description: 'Pull raw data directly from any partner API — configure request body, filters and dimensions to fetch and preview reporting data in real time',
    healthCheck: async () => {
      const { data } = await axios.get('/api/partner-configs');
      if (!Array.isArray(data)) throw new Error('Bad response');
    }
  },
  {
    path: '/report-analyzer',
    label: 'Report Analyzer',
    icon: BrainCircuit,
    tier: 'pro',
    color: 'purple',
    beta: true,
    description: 'Upload any ad server or SSP report — AI audits fill rate, eCPM, revenue opportunities and gives you a ranked action plan with specific recommendations',
    healthCheck: async () => {
      const { data } = await axios.get('/api/report-analyzer/health');
      if (data.status !== 'ok') throw new Error('Not ready');
    }
  },
];

const COLOR_MAP = {
  cyan:   { bg: 'color-mix(in srgb, var(--cyan) 6%, transparent)',   border: 'color-mix(in srgb, var(--cyan) 20%, transparent)',   text: 'var(--cyan)'   },
  green:  { bg: 'color-mix(in srgb, var(--green) 6%, transparent)',   border: 'color-mix(in srgb, var(--green) 20%, transparent)',   text: 'var(--green)'  },
  amber:  { bg: 'color-mix(in srgb, var(--amber) 6%, transparent)',   border: 'color-mix(in srgb, var(--amber) 20%, transparent)',   text: 'var(--amber)'  },
  purple: { bg: 'color-mix(in srgb, var(--purple) 6%, transparent)', border: 'color-mix(in srgb, var(--purple) 20%, transparent)', text: 'var(--purple)' },
  red:    { bg: 'color-mix(in srgb, var(--red) 6%, transparent)',   border: 'color-mix(in srgb, var(--red) 20%, transparent)',   text: 'var(--red)'    },
};

// ─── Tool card ────────────────────────────────────────────────────────────────
const LIVE_TONES = {
  red:   { bg: 'color-mix(in srgb, var(--red) 15%, transparent)',  border: 'color-mix(in srgb, var(--red) 30%, transparent)',  text: 'var(--red)'   },
  amber: { bg: 'color-mix(in srgb, var(--amber) 12%, transparent)',  border: 'color-mix(in srgb, var(--amber) 30%, transparent)',  text: 'var(--amber)' },
  green: { bg: 'color-mix(in srgb, var(--green) 12%, transparent)',  border: 'color-mix(in srgb, var(--green) 30%, transparent)',  text: 'var(--green)' },
  cyan:  { bg: 'color-mix(in srgb, var(--cyan) 12%, transparent)',  border: 'color-mix(in srgb, var(--cyan) 30%, transparent)',  text: 'var(--cyan)'  },
};

function ToolCard({ tool, status, onNavigate, live }) {
  const Icon = tool.icon;
  const c = COLOR_MAP[tool.color] || COLOR_MAP.cyan;
  const isOk = status === 'ok';
  const isChecking = status === 'checking';
  const isFailed = status === 'error';

  return (
    <div
      onClick={() => onNavigate(tool.path)}
      style={{
        background: isFailed ? 'color-mix(in srgb, var(--red) 4%, transparent)' : c.bg,
        border: `1px solid ${isFailed ? 'color-mix(in srgb, var(--red) 35%, transparent)' : c.border}`,
        borderRadius: 'var(--radius)',
        padding: '16px',
        cursor: 'pointer',
        transition: 'transform 0.12s, box-shadow 0.12s',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Status indicator top-right */}
      <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
        {live?.badge && (() => { const lt = LIVE_TONES[live.badge.tone] || LIVE_TONES.cyan; return (
          <span style={{ fontSize: '9px', fontWeight: 800, padding: '2px 6px', borderRadius: '3px',
            background: lt.bg, border: `1px solid ${lt.border}`, color: lt.text }}>
            {live.badge.text}
          </span>
        ); })()}
        {isChecking && <RefreshCw size={11} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} />}
        {isOk && <CheckCircle size={13} style={{ color: 'var(--green)' }} />}
        {isFailed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'color-mix(in srgb, var(--red) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 30%, transparent)', borderRadius: '3px', padding: '2px 6px' }}>
            <AlertTriangle size={10} style={{ color: 'var(--red)' }} />
            <span style={{ fontSize: '9px', color: 'var(--red)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Non Operational</span>
          </div>
        )}
        {tool.tier === 'pro' && (
          <span style={{
            fontSize: '9px', fontWeight: 700, color: 'var(--amber)',
            background: 'color-mix(in srgb, var(--amber) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--amber) 25%, transparent)',
            borderRadius: '3px', padding: '2px 5px', marginLeft: '2px'
          }}>PRO</span>
        )}
        {tool.beta && (
          <span style={{
            fontSize: '9px', fontWeight: 800, color: 'var(--purple)',
            background: 'color-mix(in srgb, var(--purple) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--purple) 35%, transparent)',
            borderRadius: '3px', padding: '2px 5px', marginLeft: '2px'
          }}>BETA</span>
        )}
      </div>

      {/* Icon */}
      <div style={{
        width: 38, height: 38, borderRadius: '10px',
        background: `${c.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: '12px'
      }}>
        <Icon size={18} style={{ color: c.text }} />
      </div>

      {/* Label */}
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '14px', color: isFailed ? 'var(--red)' : 'var(--text-primary)', marginBottom: '5px' }}>
        {tool.label}
      </div>

      {/* Description */}
      <div style={{ fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {tool.description}
      </div>

      {/* Live status line (tools with scheduled/recent activity) */}
      {live?.line && (
        <div style={{ marginTop: '10px', fontSize: '10px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Activity size={10} style={{ color: 'var(--cyan)', flexShrink: 0 }} /> {live.line}
        </div>
      )}

      {/* Failed message */}
      {isFailed && (
        <div style={{ marginTop: '10px', fontSize: '10px', color: 'var(--red)', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
          <XCircle size={10} /> Needs Fix — click to open tool
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

const SEVERITY_DISPLAY = {
  ok:       { label: 'OK',       tone: 'green' },
  warning:  { label: 'Warning',  tone: 'amber' },
  critical: { label: 'Critical', tone: 'red'   },
  error:    { label: 'Error',    tone: 'red'   },
};

function parseAlertData(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { company } = useAuth();
  const [statuses, setStatuses] = useState({});
  const [lastChecked, setLastChecked] = useState(null);
  const [checking, setChecking] = useState(false);

  // Briefing data — null means "not loaded (yet/failed)"; every panel degrades
  // independently so one failed endpoint never breaks the page.
  const [alerts, setAlerts] = useState(null);
  const [alertsUnread, setAlertsUnread] = useState(0);
  const [totalCrawled, setTotalCrawled] = useState(null);
  const [totalBundles, setTotalBundles] = useState(null);
  const [monitors, setMonitors] = useState(null);
  const [pulseToday, setPulseToday] = useState(null);
  const [headlines, setHeadlines] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageRange, setUsageRange] = useState('today');

  useEffect(() => {
    runHealthChecks();
    loadBriefing();
    // Refresh the Bundle Alerts figure without a page reload so a crawl that
    // finishes while the dashboard is open shows up on its own. This is the TOTAL
    // alert count from recent crawls — a briefing metric, deliberately independent
    // of read state, so acknowledging notifications never erases the briefing.
    const iv = setInterval(refreshAlerts, 60000);
    return () => clearInterval(iv);
  }, []);

  // Tool Usage — refetch whenever the selected range changes (UTC windows computed server-side)
  useEffect(() => {
    let cancelled = false;
    setUsage(null);
    axios.get('/api/account/tool-usage', { params: { range: usageRange } })
      .then(({ data }) => { if (!cancelled) setUsage(Array.isArray(data?.tools) ? data.tools : []); })
      .catch(() => { if (!cancelled) setUsage([]); });
    return () => { cancelled = true; };
  }, [usageRange]);

  async function refreshAlerts() {
    try {
      const { data } = await axios.get('/api/bundles/alerts');
      setAlerts(data.alerts || []);
      setAlertsUnread(data.unreadCount || 0);
      setTotalCrawled(data.totalCrawled ?? null);
      setTotalBundles(data.totalBundles ?? null);
    } catch { /* non-fatal — keep last good values */ }
  }

  async function loadBriefing() {
    const [a, m, s, n, t] = await Promise.allSettled([
      axios.get('/api/bundles/alerts'),
      axios.get('/api/disc-monitor-v2/list'),
      axios.get('/api/news/stats'),
      axios.get('/api/news/articles', { params: { limit: 3, offset: 0 } }),
      axios.get('/api/account/support-tickets'),
    ]);
    if (a.status === 'fulfilled') {
      setAlerts(a.value.data?.alerts || []);
      setAlertsUnread(a.value.data?.unreadCount || 0);
      setTotalCrawled(a.value.data?.totalCrawled ?? null);
      setTotalBundles(a.value.data?.totalBundles ?? null);
    }
    if (m.status === 'fulfilled') setMonitors(Array.isArray(m.value.data) ? m.value.data : []);
    if (s.status === 'fulfilled') setPulseToday(s.value.data?.today ?? 0);
    if (n.status === 'fulfilled') setHeadlines(n.value.data?.articles || []);
    if (t.status === 'fulfilled') setTickets(Array.isArray(t.value.data) ? t.value.data : []);
  }

  async function runHealthChecks() {
    setChecking(true);
    const initial = {};
    TOOLS.forEach(t => { initial[t.path] = 'checking'; });
    setStatuses({ ...initial });

    await Promise.all(TOOLS.map(async tool => {
      try {
        await tool.healthCheck();
        setStatuses(prev => ({ ...prev, [tool.path]: 'ok' }));
      } catch {
        setStatuses(prev => ({ ...prev, [tool.path]: 'error' }));
      }
    }));

    setLastChecked(new Date());
    setChecking(false);
  }

  // ── Derivations ──
  const okCount = Object.values(statuses).filter(s => s === 'ok').length;
  const failedCount = Object.values(statuses).filter(s => s === 'error').length;
  const allOk = Object.keys(statuses).length > 0 && failedCount === 0;
  const freeTools = TOOLS.filter(t => t.tier === 'free');
  const proTools = TOOLS.filter(t => t.tier === 'pro');

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  // Latest Auto Monitor run across all monitors
  const latestMonitor = (monitors || [])
    .filter(m => m.lastRun)
    .sort((x, y) => new Date(y.lastRun) - new Date(x.lastRun))[0] || null;
  const sev = latestMonitor ? (SEVERITY_DISPLAY[latestMonitor.lastRunResult] || { label: 'Ran', tone: 'cyan' }) : null;

  // Attention feed: crawl recommendations first (they carry counts + reasons),
  // newest crawl first, top 3.
  const recommendations = (alerts || [])
    .filter(al => al.alert_type === 'recommendation')
    .slice(0, 3)
    .map(al => {
      const d = parseAlertData(al.alert_data);
      return {
        id: al.id,
        count: d.bundleCount || 1,
        reason: d.reason || al.alert_type,
        listName: d.listName || al.list_name || 'list',
        when: al.crawled_at,
      };
    });
  const latestCrawlAt = (alerts || [])[0]?.crawled_at || null;

  // Bundle Alerts shows the most recent crawl day's alerts ("today's" once the
  // nightly crawl has run) rather than the 15-day pile-up. Falls back to the
  // latest available crawl day if today's run hasn't happened yet.
  const latestCrawlDay = (alerts || []).reduce((max, a) => {
    const d = (a.crawled_at || '').slice(0, 10);
    return d > max ? d : max;
  }, '');
  const todaysAlerts = latestCrawlDay
    ? (alerts || []).filter(a => (a.crawled_at || '').slice(0, 10) === latestCrawlDay)
    : [];
  const todaysCount = todaysAlerts.length;
  const latestIsToday = latestCrawlDay === new Date().toISOString().slice(0, 10);
  const crawlDayLabel = latestCrawlDay
    ? new Date(latestCrawlDay + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : '';

  // ─── Monitors sorted highest-discrepancy first (severity bucket, then the
  // numeric discrepancy when the backend provides it, then most recent run) ───
  const SEVERITY_RANK = { critical: 4, error: 3, warning: 2, ok: 1 };
  const sortedMonitors = (monitors || [])
    .filter(m => m.lastRun)
    .sort((a, b) => {
      const ra = SEVERITY_RANK[a.lastRunResult] || 0;
      const rb = SEVERITY_RANK[b.lastRunResult] || 0;
      if (rb !== ra) return rb - ra;
      const da = Number.isFinite(a.lastRunDiscrepancy) ? a.lastRunDiscrepancy : -1;
      const db = Number.isFinite(b.lastRunDiscrepancy) ? b.lastRunDiscrepancy : -1;
      if (db !== da) return db - da;
      return new Date(b.lastRun) - new Date(a.lastRun);
    });

  // Split the sorted monitors into the two dashboard cards: scheduled Auto
  // Monitors vs everything else (Manual Monitors). Each keeps the severity sort.
  const autoMonitors   = sortedMonitors.filter(m => m.monitorType === 'auto_monitor');
  const manualMonitors = sortedMonitors.filter(m => m.monitorType !== 'auto_monitor');

  // Compact monitor-row renderer shared by both monitor cards.
  const renderMonitorList = (mlist, emptyLabel) => (
    mlist.length === 0 ? (
      <>
        <div className="stat-value" style={{ fontSize: '14px', paddingTop: '6px' }}>None</div>
        <div className="stat-sub">{emptyLabel}</div>
      </>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', marginTop: '8px' }}>
        {mlist.slice(0, 5).map(m => {
          const ms = SEVERITY_DISPLAY[m.lastRunResult] || { label: '—', tone: 'cyan' };
          const toneVar = ms.tone === 'green' ? 'var(--green)' : ms.tone === 'amber' ? 'var(--amber)' : ms.tone === 'red' ? 'var(--red)' : 'var(--text-muted)';
          const hasPct = Number.isFinite(m.lastRunDiscrepancy);
          return (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: toneVar, flexShrink: 0 }} />
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }} title={m.name}>{m.name}</span>
              {hasPct && <span style={{ color: toneVar, fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{m.lastRunDiscrepancy.toFixed(1)}%</span>}
              <span style={{ color: 'var(--text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>{timeAgo(m.lastRun)}</span>
            </div>
          );
        })}
        {mlist.length > 5 && (
          <div onClick={() => navigate('/disc-monitor')} style={{ fontSize: '10px', color: 'var(--cyan)', cursor: 'pointer' }}>
            +{mlist.length - 5} more →
          </div>
        )}
      </div>
    )
  );

  // Support ticket tallies for this company (resolved counts resolved + closed;
  // open counts open + in_progress). Computed from the existing account list.
  const ticketStats = (() => {
    const list = Array.isArray(tickets) ? tickets : [];
    const done = list.filter(t => t.status === 'resolved' || t.status === 'closed').length;
    return { raised: list.length, resolved: done, open: list.length - done };
  })();

  // Live wiring for tool cards
  const live = {
    '/bundles': alerts === null ? null : {
      badge: alertsUnread > 0 ? { text: `${alertsUnread} alert${alertsUnread !== 1 ? 's' : ''}`, tone: 'red' } : null,
      line: alerts.length > 0
        ? `last crawl ${timeAgo(latestCrawlAt)} · ${alertsUnread} unread`
        : 'no crawl alerts yet — create a DSP list to start daily checks',
    },
    '/disc-monitor': monitors === null ? null : (latestMonitor ? {
      badge: { text: sev.label, tone: sev.tone },
      line: `${latestMonitor.name} · ran ${timeAgo(latestMonitor.lastRun)}`,
    } : { line: 'no monitors yet — create one for daily discrepancy checks' }),
  };

  return (
    <div>
      {/* ── Briefing header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 className="page-title" style={{ marginBottom: '4px' }}>
            {greeting}{company?.name ? `, ${company.name}` : ''}
          </h1>
          <p className="page-subtitle" style={{ margin: 0 }}>{today} · here's what changed across your stack</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {!checking && Object.keys(statuses).length > 0 && (
            allOk
              ? <span style={{ fontSize: '11px', color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <CheckCircle size={13} /> All systems live
                </span>
              : <span style={{ fontSize: '11px', color: 'var(--red)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                  <AlertTriangle size={13} /> {failedCount} tool{failedCount > 1 ? 's' : ''} flagged
                </span>
          )}
          {lastChecked && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              checked {lastChecked.toLocaleTimeString()}
            </span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={runHealthChecks} disabled={checking}>
            <RefreshCw size={12} style={{ animation: checking ? 'spin 1s linear infinite' : 'none' }} />
            {checking ? 'Checking...' : 'Refresh Status'}
          </button>
        </div>
      </div>

      {/* ── Signal cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(225px, 1fr))', gap: '12px', marginBottom: '14px' }}>
        <div className="stat-card red">
          <div className="stat-label">Bundle Alerts</div>
          <div className={`stat-value ${todaysCount > 0 ? 'red' : 'green'}`}>{alerts === null ? '—' : todaysCount}</div>
          <div className="stat-sub">{alerts === null ? 'loading…' : !latestCrawlDay ? 'no crawls yet' : latestIsToday ? "from today's crawl" : `from latest crawl · ${crawlDayLabel}`}</div>
          {((totalBundles !== null && totalBundles > 0) || (totalCrawled !== null && totalCrawled > 0)) && (
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
              {totalBundles !== null && totalBundles > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Package size={12} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {totalBundles.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>bundles tracked</span>
                </div>
              )}
              {totalCrawled !== null && totalCrawled > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: totalBundles > 0 ? '4px' : 0 }}>
                  <Globe size={12} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                    {totalCrawled.toLocaleString()}
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>checks run</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="stat-card cyan">
          <div className="stat-label">Auto Monitors</div>
          {monitors === null
            ? <div className="stat-value" style={{ fontSize: '14px', paddingTop: '6px' }}>—</div>
            : renderMonitorList(autoMonitors, 'none scheduled yet')}
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Manual Monitors</div>
          {monitors === null
            ? <div className="stat-value" style={{ fontSize: '14px', paddingTop: '6px' }}>—</div>
            : renderMonitorList(manualMonitors, 'none run yet')}
        </div>
        <div className="stat-card green">
          <div className="stat-label">Tools &amp; Pulse</div>
          <div className={`stat-value ${failedCount > 0 ? 'red' : 'green'}`}>{checking ? '…' : `${okCount}/${TOOLS.length}`}</div>
          <div className="stat-sub">{failedCount > 0 ? 'click a flagged card below' : 'tools operational'}</div>
          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Newspaper size={12} style={{ color: 'var(--amber)', flexShrink: 0 }} />
            <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{pulseToday === null ? '—' : pulseToday}</span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>new stories today</span>
          </div>
        </div>
        <div className="stat-card amber">
          <div className="stat-label">Support</div>
          {tickets === null ? (
            <div className="stat-value" style={{ fontSize: '14px', paddingTop: '6px' }}>—</div>
          ) : (
            <>
              <div className="stat-value">{ticketStats.raised}</div>
              <div className="stat-sub">{ticketStats.raised === 0 ? 'no tickets yet' : ticketStats.raised === 1 ? 'ticket raised' : 'tickets raised'}</div>
              {ticketStats.raised > 0 && (
                <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <CheckCircle size={12} style={{ color: 'var(--green)', flexShrink: 0 }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{ticketStats.resolved}</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>resolved</span>
                  </div>
                  {ticketStats.open > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                      <Bell size={12} style={{ color: 'var(--amber)', flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{ticketStats.open}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>open</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Attention + Pulse panels ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: '14px', marginBottom: '24px' }}>
        <div className="card" style={{ margin: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div className="card-title" style={{ margin: 0 }}><Bell size={13} /> Needs Attention</div>
            {latestCrawlAt && (
              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>last crawl {timeAgo(latestCrawlAt)}</span>
            )}
          </div>
          {alerts === null ? (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Loading…</div>
          ) : recommendations.length === 0 ? (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckCircle size={14} style={{ color: 'var(--green)', flexShrink: 0 }} />
              {alerts.length === 0
                ? 'No alerts yet — create a DSP list in Bundle Tracker to start daily app-ads.txt and store checks.'
                : 'All clear — no action items from recent crawls.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {recommendations.map(r => (
                <div key={r.id} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <span className={`badge ${r.reason === 'line_missing' ? 'badge-amber' : 'badge-red'}`} style={{ fontSize: '10px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {r.count} app{r.count !== 1 ? 's' : ''}
                  </span>
                  <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                    {r.reason === 'line_missing' ? 'missing the DSP line' : 'no longer found on app stores'}
                    {' — '}
                    <span style={{ color: 'var(--text-secondary)' }}>{r.listName}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div onClick={() => navigate('/bundles')}
            style={{ marginTop: '12px', fontSize: '11px', color: 'var(--cyan)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            Open Bundle Tracker →
          </div>
        </div>

        <div className="card" style={{ margin: 0 }}>
          <div className="card-title" style={{ marginBottom: '10px' }}><Newspaper size={13} /> Industry Pulse</div>
          {headlines === null ? (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Loading…</div>
          ) : headlines.length === 0 ? (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No stories yet — the feed refreshes every few hours.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {headlines.map(h => (
                <div key={h.id} style={{ fontSize: '12px', lineHeight: 1.45 }}>
                  <span style={{ color: 'var(--text-primary)' }}>{h.title}</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '6px', whiteSpace: 'nowrap' }}>
                    {h.source_name} · {timeAgo(h.published_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div onClick={() => navigate('/industry-pulse')}
            style={{ marginTop: '12px', fontSize: '11px', color: 'var(--cyan)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            All stories →
          </div>
        </div>
      </div>

      {/* ── Tool Usage ── */}
      <div className="card" style={{ margin: '0 0 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <div className="card-title" style={{ margin: 0 }}><Activity size={13} /> Tool Usage</div>
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {USAGE_RANGES.map(([key, label]) => (
              <button key={key} onClick={() => setUsageRange(key)} style={{
                fontSize: '11px', padding: '4px 9px', borderRadius: '6px', cursor: 'pointer',
                border: '1px solid ' + (usageRange === key ? 'var(--cyan)' : 'var(--border)'),
                background: usageRange === key ? 'color-mix(in srgb, var(--cyan) 10%, transparent)' : 'transparent',
                color: usageRange === key ? 'var(--cyan)' : 'var(--text-secondary)',
                fontWeight: usageRange === key ? 600 : 400,
              }}>{label}</button>
            ))}
          </div>
        </div>
        {usage === null ? (
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Loading…</div>
        ) : usage.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No tool activity in this period.</div>
        ) : (
          <div style={{ overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 56px 92px 84px', minWidth: isMobile ? 440 : 'auto', gap: '12px', padding: '0 0 8px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' }}>
              <span>Tool</span>
              <span style={{ textAlign: 'right' }}>Runs</span>
              <span style={{ textAlign: 'right' }}>Total</span>
              <span style={{ textAlign: 'right' }}>Avg</span>
            </div>
            {usage.map(t => {
              const meta = USAGE_TOOLS[t.tool] || { label: t.tool, unit: 'items', Icon: Activity };
              const Icon = meta.Icon;
              const volume = t.tool === 'bid_request'
                ? `${t.items.toLocaleString()} analyzed · ${t.items2.toLocaleString()} compared`
                : `${t.items.toLocaleString()} ${meta.unit}`;
              return (
                <div key={t.tool} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 56px 92px 84px', minWidth: isMobile ? 440 : 'auto', gap: '12px', alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                    <Icon size={15} style={{ color: 'var(--cyan)', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>{meta.label}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{volume}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{t.jobs.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{fmtDuration(t.totalMs)}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{fmtDuration(t.avgMs)}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Health-failure banner (only when something is actually broken) ── */}
      {!checking && failedCount > 0 && (
        <div style={{
          marginBottom: '20px', padding: '12px 16px', borderRadius: 'var(--radius)',
          background: 'color-mix(in srgb, var(--red) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 30%, transparent)',
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--red)', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
            <strong style={{ color: 'var(--red)' }}>{failedCount} tool{failedCount > 1 ? 's' : ''}</strong> {failedCount > 1 ? 'are' : 'is'} flagged as <strong style={{ color: 'var(--red)' }}>Non Operational</strong> — click the tool card to investigate
          </span>
        </div>
      )}

      {/* ── Free tools ── */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Activity size={11} /> Free Tools
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {freeTools.map(tool => (
            <ToolCard key={tool.path} tool={tool} status={statuses[tool.path]} onNavigate={navigate} live={live[tool.path]} />
          ))}
        </div>
      </div>

      {/* ── Pro tools ── */}
      <div>
        <div style={{ fontSize: '10px', color: 'var(--amber)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Lock size={11} /> Pro Tools
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
          {proTools.map(tool => (
            <ToolCard key={tool.path} tool={tool} status={statuses[tool.path]} onNavigate={navigate} live={live[tool.path]} />
          ))}
        </div>
      </div>
    </div>
  );
}
